/**
 * The keyboard (spec 08 §3). Keys are tracked by `KeyboardEvent.code`, so layouts do not matter.
 * Player 1: arrows or A/D, jump Up/W/Z, blow Space/X. Player 2: J/L, jump I, blow K. Both are
 * rebindable (the options' CONTROLS screen); these are the defaults.
 * (The C64 used a one-button joystick with UP to jump; Up does that here too.)
 */

export interface KeyBindings {
  left: readonly string[];
  right: readonly string[];
  jump: readonly string[];
  fire: readonly string[];
}

export const DEFAULT_KEYS: readonly [KeyBindings, KeyBindings] = [
  { left: ['ArrowLeft', 'KeyA'], right: ['ArrowRight', 'KeyD'], jump: ['ArrowUp', 'KeyW', 'KeyZ'], fire: ['Space', 'KeyX'] },
  { left: ['KeyJ'], right: ['KeyL'], jump: ['KeyI'], fire: ['KeyK'] },
];

/** Keys the menus use besides the players' own: the browser must not scroll or act on these either. */
const MENU_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space', 'Enter', 'Backspace', 'Tab'];

/** Every key the game uses under `bindings`, so the browser does not scroll or act on them. */
export function gameKeys(bindings: readonly KeyBindings[]): Set<string> {
  return new Set([...MENU_KEYS, ...bindings.flatMap((b) => [...b.left, ...b.right, ...b.jump, ...b.fire])]);
}

export class Keyboard {
  private readonly down = new Set<string>();
  /** Keys pressed since the last `endFrame`, in order (so a tap shorter than a frame still counts). */
  private readonly pressed = new Set<string>();
  private captured = gameKeys(DEFAULT_KEYS);

  constructor(target: Window = window) {
    target.addEventListener('keydown', (e) => {
      if (this.captured.has(e.code)) e.preventDefault();
      if (!e.repeat) this.pressed.add(e.code);
      this.down.add(e.code);
    });
    target.addEventListener('keyup', (e) => this.down.delete(e.code));
    // Losing focus would otherwise leave keys stuck down.
    target.addEventListener('blur', () => this.down.clear());
  }

  /** The players' keys changed: capture those from now on. */
  setBindings(bindings: readonly KeyBindings[]): void {
    this.captured = gameKeys(bindings);
  }

  /** Is any of `codes` held, or was it tapped since the last frame? */
  held(codes: readonly string[]): boolean {
    return codes.some((c) => this.down.has(c) || this.pressed.has(c));
  }

  /** Is any of `codes` held down right now (taps not counted)? */
  isDown(codes: readonly string[]): boolean {
    return codes.some((c) => this.down.has(c));
  }

  wasPressed(code: string): boolean {
    return this.pressed.has(code);
  }

  /** Was any of `codes` tapped since the last frame? */
  anyPressed(codes: readonly string[]): boolean {
    return codes.some((c) => this.pressed.has(c));
  }

  /** The first key tapped since the last frame, or null. */
  firstPressed(): string | null {
    for (const c of this.pressed) return c;
    return null;
  }

  /** Was `code` tapped? The tap is used up, so a toggle (pause) fires once however long it is kept. */
  take(code: string): boolean {
    return this.pressed.delete(code);
  }

  /** The taps have been read by the game: forget them. */
  endFrame(): void {
    this.pressed.clear();
  }
}
